import { ArrowRepeat } from 'react-bootstrap-icons';
import { observer } from 'mobx-react-lite';

import { Info } from 'components/base/Info';
import { EditNumber } from 'components/forms/EditNumber';
import { Field } from 'components/forms/Field';

import type { EditableNodeDefinition } from './EditableNodeDefinition';
import { ReturnCodeChips } from './ReturnCodeChips';
import { AutoRestartLimitsShell } from './AutoRestartLimitsShell';
import type { AutoRestartTimeline } from './autoRestartView';
import { renderAutoRestartSummary } from './renderAutoRestartSummary';

import './AutoRestartCard.scss';

const Arrow = () => (
  <span className="auto-restart__arrow" aria-hidden>
    →
  </span>
);

// The retry cycle shown once with a repeat badge. Decorative: the summary sentence above carries the same information for
// assistive tech, so the whole strip is aria-hidden.
const Timeline = ({ timeline }: { timeline: AutoRestartTimeline }) => (
  <div className="auto-restart__flow" aria-hidden>
    <span className="auto-restart__node auto-restart__node--fail">
      <span className="auto-restart__dot" />
      {timeline.failLabel}
    </span>
    <Arrow />
    <span className="auto-restart__loop">
      {timeline.showWait && (
        <>
          <span className="auto-restart__node auto-restart__node--wait">
            <span className="auto-restart__dot" />
            {timeline.waitLabel}
          </span>
          <Arrow />
        </>
      )}
      <span className="auto-restart__node auto-restart__node--retry">
        <span className="auto-restart__dot" />
        {timeline.retryLabel}
      </span>
      {timeline.badge && (
        <span className="auto-restart__badge">
          <ArrowRepeat aria-hidden />
          {timeline.badge}
        </span>
      )}
    </span>
    <Arrow />
    <span className="auto-restart__node auto-restart__node--final">{timeline.finalLabel}</span>
  </div>
);

interface Props {
  readonly entity: EditableNodeDefinition;
}

/**
 * The Automatic-restart card on the node-definition Properties tab: an enable switch in the header with a status pill, and
 * (when on) a live plain-language summary, a retry timeline, an optional return-code filter, and the max/delay inputs. The
 * header/switch/off-note shell is shared with the loop-edge card through `AutoRestartLimitsShell`; this component owns only
 * the fields inside it. A pure `observer` over the definition's auto-restart fields — every control reads a getter and
 * writes a bound setter, no local form state; the summary/timeline come from the model's `autoRestartView` computed. Shown
 * only for non-flow node types (the caller gates on `type`).
 */
export const AutoRestartCard = observer<Props>(({ entity }) => {
  const enabled = entity.autoRestartEnabled;
  const view = entity.autoRestartView;

  return (
    <AutoRestartLimitsShell
      enabled={enabled}
      switchId="auto-restart-enabled"
      switchAriaLabel="Enable automatic restart"
      title="Automatic restart"
      subtitle={enabled ? 'Retry this node when its run fails' : 'A failed run is final'}
      pillOnLabel="Triggers on: Failed"
      pillOffLabel="Off"
      offNote="Automatic restart is off — a failed run is final and no retry is attempted."
      onEnableChange={(checked) => {
        entity.autoRestartEnabled = checked;
      }}
    >
      {view ? (
        <>
          <Info>{renderAutoRestartSummary(view.summary)}</Info>
          {view.timeline && <Timeline timeline={view.timeline} />}
          <Field
            label={
              <>
                Only restart on return codes <span className="auto-restart__optional">(optional)</span>
              </>
            }
            helpText={
              <>
                Leave empty to restart on <strong>any</strong> failure. Add one or more exit codes to restrict.
              </>
            }
          >
            <ReturnCodeChips codes={entity.restartOnReturnCodes} onChange={(v) => (entity.restartOnReturnCodes = v)} ariaLabel="Only restart on return codes" />
          </Field>
          <div className="auto-restart__grid">
            <Field label="Max auto-restarts" helpText="0 = don't retry">
              <EditNumber value={entity.autoRestartAmount} min={0} onChange={(v) => (entity.autoRestartAmount = v ?? 0)} />
            </Field>
            <Field label="Delay between retries (seconds)" helpText="Wait before each retry">
              <EditNumber value={entity.autoRestartDelaySeconds} min={0} onChange={(v) => (entity.autoRestartDelaySeconds = v ?? 0)} />
            </Field>
          </div>
        </>
      ) : null}
    </AutoRestartLimitsShell>
  );
});
